import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';

import { ApiPublicService } from '../../../../../lib/lib-ngx/web-services/api-public.service';
import { IKit } from '../../../../../lib/lib-shared/models/Kit';
import { PRICEMULTIPLIER } from '../../../../../lib/lib-shared/defines';
import { IHouseDetailsList } from '../../../../../lib/lib-shared/models/House';
import { IKitBatchOrderPayload, IKitOrderPayload, TKitOrderEmit } from './user-profile-kit-batch.types';

@Component({
  selector: 'lm-user-profile-kit-batch',
  templateUrl: './user-profile-kit-batch.component.html',
  styleUrls: ['./user-profile-kit-batch.component.scss']
})
export class UserProfileKitBatchComponent implements OnInit {

  @Input() public houseList: IHouseDetailsList[] = [];
  @Input() public isKitMode = false;
  public kitList: IKit[];

  public orderKitDetailsForm: FormGroup;

  @Output() public cancel = new EventEmitter<any>();
  @Output() public order = new EventEmitter<TKitOrderEmit>();

  get orders(): FormArray {
    return this.orderKitDetailsForm.controls['orders'] as FormArray;
  }

  constructor(
    private apiPublicService: ApiPublicService,
    private fb: FormBuilder,
  ) {
  }

  ngOnInit() {
    this.apiPublicService.getKit({})
      .subscribe(kitList => {
        this.kitList = kitList;
        this.buildForm();
      });
  }

  kitMinQuantity(group: FormGroup): null | ValidationErrors {
    let hasError = false;
    let total = 0;
    for (const id of Object.keys(group.value)) {
      if (group.value[id] < 0) {
        hasError = true;
      }
      total += group.value[id];
    }

    if (hasError || total === 0) {
      return {
        validValidator: group
      };
    }

    return null;
  }

  private getDefaultHouseId(): number | null {
    if (!this.houseList.length) {
      return null;
    }
    const defaultHouse = this.houseList.find(house => house.default);
    return defaultHouse ? defaultHouse.id : this.houseList[0].id;
  }

  private buildRow(): FormGroup {
    const kitListGroup = this.fb.group(
      this.kitList.reduce((acc, kit) => ({...acc, [kit.id]: this.fb.control(0, Validators.required)}), {}),
      {validator: this.kitMinQuantity}
    );
    return this.fb.group({
      houseId: this.fb.control(this.getDefaultHouseId(), Validators.required),
      kitList: kitListGroup,
    });
  }

  buildForm() {
    const houseId = this.getDefaultHouseId();
    if (!this.isKitMode) {
      this.orderKitDetailsForm = this.fb.group({});
      this.orderKitDetailsForm.addControl('shippingHouseId', this.fb.control(houseId, Validators.required));
      this.orderKitDetailsForm.addControl('kitList', this.fb.group({}, {validator: this.kitMinQuantity}));
      const kitListForm = this.orderKitDetailsForm.get('kitList') as FormGroup;
      for (const kit of this.kitList) {
        kitListForm.addControl(kit.id.toString(), this.fb.control(0, Validators.required));
      }
    } else {
      this.orderKitDetailsForm = this.fb.group({
        shippingHouseId: this.fb.control(houseId, Validators.required),
        orders: this.fb.array([this.buildRow()]),
        free: this.fb.control(false, Validators.required),
      });
    }
  }

  addRow() {
    this.orders.push(this.buildRow());
  }

  removeRow(index: number) {
    this.orders.removeAt(index);
  }

  onOrder() {
    this.order.emit(this.isKitMode ? this.buildKitOrderPayload() : this.buildKitBatchOrderPayload());
  }

  private buildKitBatchOrderPayload(): IKitBatchOrderPayload {
    return {
      shippingHouseId: this.orderKitDetailsForm.value.shippingHouseId,
      kitList: this.kitList.map(kit => ({
        kitId: kit.id,
        quantity: this.orderKitDetailsForm.value.kitList[kit.id] * PRICEMULTIPLIER,
      })),
    };
  }

  private buildKitOrderPayload(): IKitOrderPayload {
    return {
      free: this.orderKitDetailsForm.value.free,
      shippingHouseId: this.orderKitDetailsForm.value.shippingHouseId,
      kitList: (this.orderKitDetailsForm.value.orders as any[]).reduce((acc, row) =>
        acc.concat(this.kitList.map(kit => ({
          kitId: kit.id,
          houseId: row.houseId,
          quantity: row.kitList[kit.id] * PRICEMULTIPLIER,
        }))), []
      ),
    };
  }
}
